import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import { formatCatalogYearly } from '@/content/subscriptionPricing';
import {
  DISABLED_PLAN_LAUNCH_NOTE,
  clampFamilyPlanConfig,
  findPlayerPlan,
  isPlanEnabled,
  listAllPlanOptions,
  planChangeKind,
  planFromPrice,
  quoteFamilyPlan,
  type PlayerFamilyCatalog,
} from '@/content/subscriptionCatalog';
import type { SubscriptionOffer } from '@/content/subscriptionPricing';
import { changeSubscriptionPlan } from '@services/subscriptionService';
import PrebookDialog from '@components/billing/PrebookDialog';
import type { PlayerPlanId, SubscriptionCategory, UserSubscription } from '@types/index';

interface ChangePlanDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: UserSubscription | null;
  catalog: PlayerFamilyCatalog;
  offer?: SubscriptionOffer;
  onChanged: (next: UserSubscription) => void;
}

const ChangePlanDialog: React.FC<ChangePlanDialogProps> = ({
  open,
  onClose,
  subscription,
  catalog,
  offer,
  onChanged,
}) => {
  const { user } = useAuthStore();
  const current = clampFamilyPlanConfig(subscription?.planConfig, catalog);
  const options = useMemo(() => listAllPlanOptions(catalog), [catalog]);
  const [pending, setPending] = useState<{ category: SubscriptionCategory; planId: PlayerPlanId } | null>(
    null,
  );
  const [prebookTarget, setPrebookTarget] = useState<{
    category: SubscriptionCategory;
    planId: PlayerPlanId;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const pendingQuote = useMemo(() => {
    if (!pending) return null;
    return quoteFamilyPlan(
      offer,
      {
        ...current,
        category: pending.category,
        planId: pending.planId,
      },
      catalog,
      { fromCategory: current.category },
    );
  }, [pending, offer, current, catalog]);

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    setError('');
    try {
      const next = await changeSubscriptionPlan({
        category: pending.category,
        planId: pending.planId,
        familyConfig: {
          ...current,
          category: pending.category,
          planId: pending.planId,
        },
      });
      onChanged(next);
      setPending(null);
      onClose();
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(anyErr.response?.data?.message || anyErr.message || 'Could not change plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 800, color: brandColors.navy }}>
        Upgrade / Downgrade
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.5 }}>
          All options are yearly. Your current plan is unavailable. Individual → Family applies the admin
          family upgrade discount.
        </Typography>
        <Stack spacing={1}>
          {options.map((option) => {
            const kind = planChangeKind(current, option);
            const plan = findPlayerPlan(option.planId, catalog);
            const from = planFromPrice(plan, catalog, offer, option.category);
            const planOff = !isPlanEnabled(plan, option.category);
            const disabled = kind === 'current' || planOff;
            return (
              <Box
                key={`${option.category}-${option.planId}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.15,
                  borderRadius: 1.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: disabled ? '#f1f5f9' : brandColors.white,
                  opacity: disabled ? 0.62 : 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.92rem' }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block' }}>
                    {planOff
                      ? 'Price available when this plan launches'
                      : `${formatCatalogYearly(from.payableUsd, catalog.currency)} / year`}
                  </Typography>
                  {planOff && kind !== 'current' && (
                    <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mt: 0.25 }}>
                      {DISABLED_PLAN_LAUNCH_NOTE}
                    </Typography>
                  )}
                </Box>
                {planOff && kind !== 'current' ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPrebookTarget({ category: option.category, planId: option.planId })}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      minWidth: 132,
                      borderColor: brandColors.navy,
                      color: brandColors.navy,
                    }}
                  >
                    Pre-book
                  </Button>
                ) : (
                  <Button
                    size="small"
                    disabled={disabled}
                    variant={kind === 'upgrade' ? 'contained' : 'outlined'}
                    onClick={() => setPending({ category: option.category, planId: option.planId })}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      minWidth: 132,
                      bgcolor: kind === 'upgrade' ? brandColors.navy : undefined,
                      color: kind === 'downgrade' ? brandColors.navy : undefined,
                      borderColor: kind === 'downgrade' ? brandColors.navy : undefined,
                      '&:hover': kind === 'upgrade' ? { bgcolor: '#0a0f28' } : undefined,
                    }}
                  >
                    {kind === 'current' ? 'Current plan' : kind === 'upgrade' ? 'Choose / Upgrade' : 'Downgrade'}
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>

        {pending && isPlanEnabled(findPlayerPlan(pending.planId, catalog), pending.category) && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: brandColors.ice, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.5 }}>
              Confirm {planChangeKind(current, pending) === 'upgrade' ? 'upgrade' : 'downgrade'}
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              {current.category === 'family' ? 'Family' : 'Individual'} {findPlayerPlan(current.planId, catalog).name}{' '}
              → {pending.category === 'family' ? 'Family' : 'Individual'} {findPlayerPlan(pending.planId, catalog).name}
            </Typography>
            {pendingQuote && (
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, mt: 0.75 }}>
                {formatCatalogYearly(pendingQuote.payableUsd, catalog.currency)} / year
              </Typography>
            )}
            {pendingQuote && pendingQuote.individualToFamilyDiscountPercent > 0 && (
              <Typography variant="body2" sx={{ color: brandColors.orange, fontWeight: 700, mt: 0.35 }}>
                Individual → Family discount · {pendingQuote.individualToFamilyDiscountPercent}% · save{' '}
                {formatCatalogYearly(pendingQuote.individualToFamilyDiscountUsd, catalog.currency)}
              </Typography>
            )}
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.slate }}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={
            !pending ||
            saving ||
            !isPlanEnabled(findPlayerPlan(pending.planId, catalog), pending.category)
          }
          onClick={confirm}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: brandColors.navy,
            '&:hover': { bgcolor: '#0a0f28' },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
    <PrebookDialog
      open={Boolean(prebookTarget)}
      onClose={() => setPrebookTarget(null)}
      catalog={catalog}
      offer={offer}
      initialConfig={{
        ...current,
        category: prebookTarget?.category || current.category,
        planId: prebookTarget?.planId || current.planId,
      }}
      defaultContact={user?.email}
      onPrebooked={(next) => {
        onChanged(next);
        setPrebookTarget(null);
        onClose();
      }}
    />
    </>
  );
};

export default ChangePlanDialog;
